import { HomeIcon } from '@heroicons/react/24/solid';
import React, { useMemo } from 'react';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';

import { MODULES } from '../../../../utils/mainModules';
import { ROUTES } from '../../../../utils/routes';
import { ROUTE_TITLES } from '../../../../utils/titles';

interface BreadcrumbItem {
  title: string;
  link: string;
}

const Breadcrumb: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const path = location.pathname;

  const breadcrumbItems = useMemo(() => {
    const items: BreadcrumbItem[] = [{ title: ROUTE_TITLES[ROUTES.EXTERNAL_ACCUEIL], link: ROUTES.EXTERNAL_ACCUEIL }, { title: ROUTE_TITLES[ROUTES.HOME], link: ROUTES.HOME }];

    const currentModule = MODULES.find(module => {
      const normalizedLocation = location.pathname.replace(/^\//, '');
      const normalizedItemHref = module.route.replace(/^\//, '');
      return normalizedLocation.startsWith(normalizedItemHref);
    });

    if (currentModule && currentModule.route !== ROUTES.HOME) {
      items.push({ title: currentModule.title, link: currentModule.route });
    }

    // Modified route matching logic
    const matchingRoute = Object.entries(ROUTES).find(([, route]) => {
      const matchResult = matchPath({ path: route, end: true }, path);
      return matchResult !== null;
    });

    if (matchingRoute) {
      const [, route] = matchingRoute;
      const pageTitle = ROUTE_TITLES[route as keyof typeof ROUTES];
      if (pageTitle && (!currentModule || pageTitle !== currentModule.title) && route !== ROUTES.HOME) {
        items.push({ title: pageTitle, link: path }); // Use actual path for dynamic routes
      }
    }

    return items;
  }, [path, location.pathname]);

  const currentPageTitle = breadcrumbItems[breadcrumbItems.length - 1]?.title || '';

  const handleMainModuleClick = (route: string) => {
    navigate(route);
  };

  return (
    <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-rgpd-blue to-rgpd-blue-light p-8 text-white shadow-lg lg:p-10">
      <div className="pointer-events-none absolute -top-1/2 -right-[10%] h-[400px] w-[400px] rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -bottom-[30%] -left-[5%] h-[300px] w-[300px] rounded-full bg-white/5" />
      <div className="relative z-10">
        <nav className="mb-3 text-sm" aria-label="Breadcrumb">
          <ol className="inline-flex list-none flex-wrap items-center gap-y-1 p-0">
            <li className="flex items-center">
              <Link to={ROUTES.EXTERNAL_ACCUEIL} className="flex items-center opacity-90 hover:underline hover:opacity-100">
                <HomeIcon className="h-4 w-4" />
              </Link>
            </li>
            {breadcrumbItems.map((item, index) => (
              <li key={index} className="flex flex-wrap items-center">
                <span className="mx-2 whitespace-nowrap opacity-60">/</span>
                {index < breadcrumbItems.length - 1 ? (
                  Object.keys(MODULES).some(key => MODULES[key].title === item.title) ? (
                    <button
                      onClick={() => handleMainModuleClick(item.link)}
                      className="whitespace-nowrap opacity-90 hover:underline hover:opacity-100"
                    >
                      {item.title}
                    </button>
                  ) : (
                    <Link to={item.link} className="whitespace-nowrap opacity-90 hover:underline hover:opacity-100">
                      {item.title}
                    </Link>
                  )
                ) : (
                  <span className="whitespace-nowrap opacity-90" aria-current="page">{item.title}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
        <h1 className="text-[28px] leading-tight font-bold">{currentPageTitle}</h1>
      </div>
    </div>
  );
};

export default Breadcrumb;
